import { usePage } from '@inertiajs/react'
import { ChangeClientUrl, Navigation, NavigationPageProps, PageProps } from '@/types'
import { NavigationItem } from '@/types/global'
import {
    ArchiveBoxXMarkIcon,
    CogIcon,
    CurrencyEuroIcon,
    DocumentCurrencyEuroIcon,
    DocumentDuplicateIcon,
    DocumentIcon,
    FolderIcon,
    HomeIcon,
    InboxStackIcon,
    Square3Stack3DIcon,
    TicketIcon,
    UserIcon,
    UsersIcon,
} from '@heroicons/react/24/outline'
import { QuestionMarkCircleIcon } from '@heroicons/react/20/solid'
import { Abilities, User } from '@/types/models'
import { Method } from '@inertiajs/core'

export function useNavigation(changeClientUrl: ChangeClientUrl | undefined): Navigation {
    const page = usePage<PageProps>()
    const user = page.props.auth.user
    const abilities = user.site_abilities
    const navigationPageProps = page.props.navigation

    const userNavigation = [
        { name: 'Mein Profil', href: route('profile.edit'), method: 'get' as Method },
        { name: 'Ausloggen', href: route('logout'), method: 'post' as Method },
    ]

    return {
        pageProps: navigationPageProps,
        displayClientNavigation: shouldDisplayClientNavigation(navigationPageProps, user, changeClientUrl),
        sections: {
            global: buildGlobalNavigation(abilities, user, navigationPageProps),
            client: buildClientScopedSubNavigation(abilities, navigationPageProps),
            bottom: buildBottomNavigation(abilities),
            user: userNavigation,
        },
    }
}

function buildBottomNavigation(abilities: Abilities) {
    const currentRoute = route().current()
    const bottom: NavigationItem[] = []

    if (abilities?.canViewConsultantHelp) {
        bottom.push({
            name: 'Hilfe',
            href: route('documentation.tax-consultant'),
            asLink: false,
            icon: QuestionMarkCircleIcon,
            current: false,
        })
    }

    if (abilities?.canViewClientHelp) {
        bottom.push({
            name: 'Hilfe',
            href: route('documentation.tax-consultant-client'),
            asLink: false,
            icon: QuestionMarkCircleIcon,
            current: false,
        })
    }

    if (abilities?.canViewTaxConsultantUsageAndBilling) {
        bottom.push({
            name: 'Nutzung & Abrechnung',
            href: route('subscriptions.billing.index'),
            asLink: true,
            icon: TicketIcon,
            current: !!currentRoute?.startsWith('subscriptions.billing.'),
        })
    }

    return bottom
}

function shouldDisplayClientNavigation(
    navigationPageProps: NavigationPageProps,
    user: User,
    changeClientUrl: ChangeClientUrl | undefined
): boolean {
    let displayClientNavigation = !!changeClientUrl

    if (user.role === 'tax_consultant_client_user' && navigationPageProps.otherClients.length === 1) {
        displayClientNavigation = false
    }

    return displayClientNavigation
}

function buildGlobalNavigation(abilities: Abilities, user: User, navigationPageProps: NavigationPageProps) {
    const page = usePage<PageProps>()
    const currentRoute = route().current()
    const global: NavigationItem[] = []

    if (user.role === 'tax_consultant_client_user') {
        return buildClientGlobalNavigation(abilities, user, navigationPageProps)
    }

    global.push({
        name: 'Home',
        href: route('dashboard'),
        icon: HomeIcon,
        current: route().current('dashboard'),
    })

    if (abilities?.canViewAnyMonthlyClosuresIndex) {
        global.push({
            name: 'Abschlüsse',
            href: navigationPageProps.currentClient
                ? route('tax-consultant-clients.monthly-closures.index', navigationPageProps.currentClient.id)
                : route('monthly-closures.index'),
            icon: InboxStackIcon,
            current: !!currentRoute?.startsWith('monthly-closures.'),
        })
    }

    if (abilities?.canViewTaxConsultantsIndex) {
        global.push({
            name: 'Steuerberater*innen',
            href: route('tax-consultants.index'),
            icon: DocumentDuplicateIcon,
            current: !!currentRoute?.startsWith('tax-consultants.'),
        })
    } else {
        global.push({
            name: 'Meine Kanzlei',
            href: route('tax-consultants.view', { taxConsultant: user.tax_consultant_id }),
            icon: DocumentDuplicateIcon,
            current: !!currentRoute?.startsWith('tax-consultants.'),
        })
    }

    if (abilities?.canViewTaxConsultantClientsIndex) {
        global.push({
            name: 'Mandant*innen',
            href: route('tax-consultant-clients.index'),
            icon: FolderIcon,
            current:
                !!currentRoute?.startsWith('tax-consultant-clients.') ||
                !!currentRoute?.startsWith('outgoing-invoice-import-configurations.'),
        })
    }

    if (abilities?.canViewUsersIndex) {
        global.push({
            name: 'Benutzer*innen',
            href: route('users.index'),
            icon: UsersIcon,
            current:
                !!currentRoute?.startsWith('users') || !!currentRoute?.startsWith('send-user-registration-invitation.'),
        })
    }

    if (abilities?.canViewAdminUsageAndBilling) {
        global.push({
            name: 'Nutzung & Abrechnung',
            href: route('subscriptions.usage-and-billing.index'),
            icon: TicketIcon,
            current: !!currentRoute?.startsWith('subscriptions.usage-and-billing'),
        })
    }

    if (page.props.hasOutgoingInvoiceImportErrors) {
        global.push({
            name: 'Importfehler',
            href: route('outgoing-invoice-import-errors.index'),
            icon: ArchiveBoxXMarkIcon,
            current: !!currentRoute?.startsWith('outgoing-invoice-import-errors.'),
        })
    }

    return global
}

function buildClientScopedSubNavigation(abilities: Abilities, navigationPageProps?: NavigationPageProps) {
    const currentRoute = route().current()
    const navigationItems: NavigationItem[] = []

    if (navigationPageProps && navigationPageProps.currentClient) {
        navigationItems.push({
            name: 'Abschlüsse',
            href: route('tax-consultant-clients.monthly-closures.index', navigationPageProps.currentClient.id),
            icon: InboxStackIcon,
            current: !!currentRoute?.startsWith('monthly-closures.'),
        })

        if (abilities?.canViewTaxConsultantClientsIndex) {
            navigationItems.push({
                name: 'Mandant*in',
                href: route('tax-consultant-clients.view', navigationPageProps.currentClient.id),
                icon: UserIcon,
                current:
                    !!currentRoute?.startsWith('tax-consultant-clients.edit') ||
                    !!currentRoute?.startsWith('tax-consultant-clients.view'),
            })
        }
        navigationItems.push({
            name: 'Dokumente',
            href: route('shared-files.index', navigationPageProps.currentClient.id),
            icon: DocumentIcon,
            current: !!route().current()?.startsWith('shared-files.'),
        })

        if (navigationPageProps.abilities.canViewGeneralLedgerAccounts) {
            navigationItems.push({
                name: 'Sachkonten',
                href: route(
                    'tax-consultant-client-general-ledger-accounts.index',
                    navigationPageProps.currentClient.id
                ),
                icon: Square3Stack3DIcon,
                current: !!currentRoute?.startsWith('tax-consultant-client-general-ledger-accounts.'),
            })
        }

        navigationItems.push({
            name: 'Kund*innen',
            href: route('tax-consultant-client-debtors.index', navigationPageProps.currentClient.id),
            icon: CurrencyEuroIcon,
            current: !!currentRoute?.startsWith('tax-consultant-client-debtors.'),
        })

        if (navigationPageProps.abilities.canViewCreditors) {
            navigationItems.push({
                name: 'Gläubiger*innen',
                href: route('tax-consultant-client-creditors.index', navigationPageProps.currentClient.id),
                icon: DocumentCurrencyEuroIcon,
                current: !!currentRoute?.startsWith('tax-consultant-client-creditors.'),
            })
        }

        if (abilities?.canViewUsersIndex) {
            navigationItems.push({
                name: 'Benutzer*innen',
                href: route('tax-consultant-client-users.index', navigationPageProps.currentClient.id),
                icon: UsersIcon,
                current: !!currentRoute?.startsWith('tax-consultant-client-users.'),
            })
        }

        if (navigationPageProps.abilities.canConfigureClosure) {
            navigationItems.push({
                name: 'Abschlusskonfiguration',
                href: route(
                    'tax-consultant-clients.monthly-closure-configuration.edit',
                    navigationPageProps.currentClient.id
                ),
                icon: CogIcon,
                current:
                    !!currentRoute?.startsWith('tax-consultant-clients.monthly-closure-configuration.edit') ||
                    !!currentRoute?.startsWith('outgoing-invoice-import-configurations.edit') ||
                    !!currentRoute?.startsWith('get-my-invoices-integration.'),
            })
        }
    }

    return navigationItems
}

function buildClientGlobalNavigation(abilities: Abilities, user: User, navigationPageProps: NavigationPageProps) {
    const currentRoute = route().current()
    const navigationItems: NavigationItem[] = []

    if (navigationPageProps.otherClients.length > 1) {
        navigationItems.push({
            name: 'Home',
            href: route('dashboard'),
            icon: HomeIcon,
            current: route().current('dashboard'),
        })
    }

    navigationItems.push({
        name: 'Abschlüsse',
        href: route('tax-consultant-clients.monthly-closures.index', navigationPageProps.currentClient!.id),
        icon: InboxStackIcon,
        current: !!currentRoute?.startsWith('monthly-closures.'),
    })

    if (navigationPageProps.otherClients.length <= 1) {
        navigationItems.push({
            name: 'Dokumente',
            href: route('shared-files.index', navigationPageProps.currentClient!.id),
            icon: DocumentIcon,
            current: !!route().current()?.startsWith('shared-files.'),
        })
    }

    navigationItems.push({
        name: 'Meine Kanzlei',
        href: route('tax-consultants.view', user.tax_consultant_id!),
        icon: DocumentDuplicateIcon,
        current: !!currentRoute?.startsWith('tax-consultants.'),
    })

    if (abilities?.canViewUsersIndex) {
        navigationItems.push({
            name: 'Benutzer*innen',
            href: route('users.index'),
            icon: UsersIcon,
            current:
                !!currentRoute?.startsWith('users') || !!currentRoute?.startsWith('send-user-registration-invitation.'),
        })
    }

    return navigationItems
}
